import React, { useState } from 'react'
import { Select } from 'antd'
import styled, { createGlobalStyle } from 'styled-components'

import { useAppDispatch } from 'src/store'
import { USER_TREATMENT_STATE } from 'src/enum/userTreatmentState'
import CommonModal from 'src/components/Modal/CommonModal'
import ConfirmPlanModal from 'src/components/Modal/ConfirmPlanModal'
import ButtonCustom from 'src/components/form/Button'
import uiActions from 'src/store/ui/notification'
import {
  cancelUserTreatment,
  getCancelModalTreatments,
} from 'src/useCase/userTreatment'
import {
  CANCEL_REASON,
  CANCEL_REASON_OPTIONS,
  CANCEL_REASON_TEXT_MAX_LENGTH,
  CancelReasonValue,
} from 'src/constants/cancelReason'

const { Option } = Select
const FAILED = 'failed'

type CancelableTreatment = {
  id: number
  menu?: { name?: string | null } | null
}

const DEFAULT_EXCLUDED_STATES = [
  USER_TREATMENT_STATE.CANCEL_REQUESTED,
  USER_TREATMENT_STATE.CANCELED,
  USER_TREATMENT_STATE.DOCTOR_CANCEL_PRESCRIPTION,
  USER_TREATMENT_STATE.DOCTOR_PRESCRIPTION_OK,
  USER_TREATMENT_STATE.SUBSCRIPTION_CONTRACT,
  // No.201: 処方NGの診療はキャンセル申請の対象外
  USER_TREATMENT_STATE.SUBSCRIPTION_SUGGESTIONS,
]

type Props = {
  onCancelSuccess?: () => void
  excludedStates?: string[]
  includedStates?: string[]
}

const CancelTreatmentSection: React.FC<Props> = ({
  onCancelSuccess,
  excludedStates = DEFAULT_EXCLUDED_STATES,
  includedStates,
}) => {
  const dispatch = useAppDispatch()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [cancelModalTreatments, setCancelModalTreatments] = useState<
    CancelableTreatment[]
  >([])
  const [isLoadingCancelModal, setIsLoadingCancelModal] = useState(false)
  const [cancelSelectedTreatmentId, setCancelSelectedTreatmentId] = useState<
    number | null
  >(null)
  const [cancelReasonCode, setCancelReasonCode] =
    useState<CancelReasonValue | null>(null)
  const [cancelReasonText, setCancelReasonText] = useState('')
  const [isSubmitCancelTreatment, setIsSubmitCancelTreatment] = useState(false)
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false)
  const [stateBefore, setStateBefore] = useState('')

  const handleOpenModal = async () => {
    setIsLoadingCancelModal(true)
    try {
      const stateFilter: Record<string, string> = includedStates?.length
        ? { state: includedStates.join(',') }
        : { not_state: excludedStates.join(',') }
      const treatments = await getCancelModalTreatments({
        with: 'menu',
        ...stateFilter,
      })
      setCancelModalTreatments((treatments ?? []) as CancelableTreatment[])
      setCancelSelectedTreatmentId(null)
      setCancelReasonCode(null)
      setCancelReasonText('')
      setIsModalOpen(true)
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        'エラーが発生しました。再度お試しください。'
      dispatch(uiActions.onNotification({ type: 'error', message }))
    } finally {
      setIsLoadingCancelModal(false)
    }
  }

  // [SCOPE] Validation stays in component state rather than moving to
  // react-hook-form + yup: the popup has no <form>, the 診療科目 select already
  // worked this way before this change, and converting it would rewrite code
  // this task does not otherwise touch.
  const isOtherReason = cancelReasonCode === CANCEL_REASON.OTHER
  const isSubmitDisabled =
    !cancelSelectedTreatmentId ||
    !cancelReasonCode ||
    (isOtherReason && !cancelReasonText.trim())

  const handleConfirmCancel = async () => {
    if (isSubmitDisabled) return
    try {
      setIsSubmitCancelTreatment(true)
      const res = await cancelUserTreatment(cancelSelectedTreatmentId!, {
        reason_code: cancelReasonCode!,
        reason_text: isOtherReason ? cancelReasonText.trim() : null,
      })
      setStateBefore(res?.data?.data?.state_before || 'success')
      setIsModalConfirmOpen(true)
    } catch {
      setStateBefore(FAILED)
      setIsModalConfirmOpen(true)
    } finally {
      setIsSubmitCancelTreatment(false)
      setIsModalOpen(false)
    }
  }

  const handleCloseConfirmModal = () => {
    setIsModalConfirmOpen(false)
    // [SCOPE] Refresh list only after user dismisses success modal.
    // Pre-existing: onCancelSuccess was called before opening the modal, causing
    // userTreatments to clear (entities=[]) and unmount this component, losing
    // isModalConfirmOpen=true before the modal could render.
    // New behavior: list refreshes after modal closes — safe, only affects this component.
    if (stateBefore && stateBefore !== FAILED) {
      onCancelSuccess?.()
    }
  }

  const handleCloseSelectModal = () => {
    setIsModalOpen(false)
  }

  const handleSelectTreatment = (value?: number) => {
    if (value) {
      setCancelSelectedTreatmentId(value)
    }
  }

  const handleSelectReason = (value?: CancelReasonValue) => {
    setCancelReasonCode(value ?? null)
    // Leaving「その他」must not keep the free text around: it would be sent
    // again if the patient came back to「その他」later in the same session.
    if (value !== CANCEL_REASON.OTHER) {
      setCancelReasonText('')
    }
  }

  return (
    <>
      <CancelReasonDropdownStyle />
      <div className="d-flex justify-center mt-80">
        <div className="d-flex justify-center mx-auto">
          <button
            onClick={handleOpenModal}
            disabled={isLoadingCancelModal}
            type="button"
            className="btn btn-line"
          >
            キャンセルのお申し込みはこちら
          </button>
        </div>
      </div>

      <ConfirmPlanModal isOpen={isModalOpen} onCancel={handleCloseSelectModal}>
        <CancelForm className="p-15 text-center mt-20">
          <h3 className="text-title">
            キャンセルをお申し込みされる内容をご選択ください
          </h3>

          <div className="cancel-field">
            <p className="cancel-field__label">
              診療科目
              <span className="cancel-field__required">必須</span>
            </p>
            <Select
              className="cancel-field__select"
              aria-label="診療科目"
              value={cancelSelectedTreatmentId ?? undefined}
              placeholder="選択してください"
              onChange={handleSelectTreatment}
            >
              {cancelModalTreatments.map((userTreatment) => (
                <Option value={userTreatment.id} key={userTreatment.id}>
                  {userTreatment.menu?.name}
                </Option>
              ))}
            </Select>
          </div>

          <div className="cancel-field">
            <p className="cancel-field__label">
              キャンセル理由
              <span className="cancel-field__required">必須</span>
            </p>
            <Select
              className="cancel-field__select"
              aria-label="キャンセル理由"
              value={cancelReasonCode ?? undefined}
              placeholder="選択してください"
              onChange={handleSelectReason}
              // antd v4 spelling; renamed to popupClassName in v5.
              dropdownClassName="cancel-reason-dropdown"
              // Nine short options do not need windowing, and the virtual list
              // keeps the ones scrolled out of view out of the DOM entirely,
              // which hides them from screen readers.
              virtual={false}
            >
              {CANCEL_REASON_OPTIONS.map((reason) => (
                <Option value={reason.value} key={reason.value}>
                  {reason.label}
                </Option>
              ))}
            </Select>
          </div>

          {isOtherReason && (
            <div className="cancel-field">
              <p className="cancel-field__label">
                詳細
                <span className="cancel-field__required">必須</span>
              </p>
              <textarea
                className="cancel-field__textarea"
                value={cancelReasonText}
                maxLength={CANCEL_REASON_TEXT_MAX_LENGTH}
                rows={4}
                placeholder="キャンセルの理由をご記入ください"
                onChange={(e) => setCancelReasonText(e.target.value)}
              />
              <p className="cancel-field__counter">
                {cancelReasonText.length} / {CANCEL_REASON_TEXT_MAX_LENGTH}
              </p>
            </div>
          )}

          <p>申し込みボタンを押すと、キャンセルのお申し込みが送信されます。</p>
          <p>キャンセルをお申し込みされる場合はボタンを押してください。</p>
          <ButtonCustom
            className="btn-default btn-confirm mt-3"
            loading={isSubmitCancelTreatment}
            disabled={isSubmitDisabled}
            onClick={handleConfirmCancel}
            htmlType="submit"
            name="キャンセルを申し込む"
            showIconRight
          />
        </CancelForm>
      </ConfirmPlanModal>

      <CommonModal
        isOpen={isModalConfirmOpen}
        onClose={handleCloseConfirmModal}
      >
        {stateBefore && stateBefore !== FAILED ? (
          <div className="p-15 text-center mt-20">
            <p>
              この度はキャンセルのお申し込みをいただき、誠にありがとうございます。
            </p>
            <p>
              現在、順次キャンセル手続きを進めております。
              <br />
              手続きの完了までに1〜3営業日ほどお時間をいただく場合がございますので、あらかじめご了承ください。
            </p>
            <p>
              手続きが完了いたしましたら、改めてメールにてご連絡いたします。
              <br />
              今しばらくお待ちいただけますよう、何卒よろしくお願い申し上げます。
            </p>
          </div>
        ) : (
          <div className="p-15 text-center mt-20">
            <h3 className="text-title">キャンセル処理ができませんでした。</h3>
            <p>以下サポート窓口までお問い合わせください。</p>
            <p>
              オンライン処方サポート：
              <a
                href="mailto:bioportplus@sheepmedical.com"
                className="color-text-blue"
              >
                bioportplus@sheepmedical.com
              </a>
            </p>
          </div>
        )}
      </CommonModal>
    </>
  )
}

/* [SCOPE] This component already mixed Bootstrap utility classes with Ant Design
   before this change, and every modal it renders into (ConfirmPlanModal,
   CommonModal) pairs antd with styled-components. The new styles follow that
   existing pattern rather than introducing a stylesheet the modal cannot reach:
   ConfirmPlanModal portals its children out of the container whose SCSS would
   otherwise apply. */
const CancelForm = styled.div`
  .cancel-field {
    text-align: left;
    margin-bottom: 18px;
  }

  .cancel-field__label {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
    color: var(--color-rangoon-green);
  }

  .cancel-field__required {
    margin-left: 8px;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 700;
    color: var(--color-white);
    background: var(--color-chestnut-rose);
    vertical-align: 1px;
  }

  .cancel-field__select {
    width: 100%;

    .ant-select-selector {
      height: 60px !important;
      padding: 0 12px !important;
    }

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 58px !important;
      font-size: 14px;
    }
  }

  .cancel-field__textarea {
    width: 100%;
    min-height: 96px;
    padding: 12px;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
    font-size: 14px;
    line-height: 1.5;
    resize: vertical;

    &::placeholder {
      color: #bfbfbf;
    }

    &:focus {
      outline: none;
      border-color: var(--color-chestnut-rose);
    }
  }

  .cancel-field__counter {
    margin: 6px 0 0;
    font-size: 12px;
    color: var(--color-limed-ash);
    text-align: right;
  }

  /* .btn-confirm forces its background with !important, so the disabled state
     needs the same weight or the button stays red while it cannot be pressed. */
  .ant-btn.btn-confirm[disabled],
  .ant-btn.btn-confirm[disabled]:hover {
    background-color: var(--color-alto) !important;
    border-color: var(--color-alto) !important;
    color: #8f8f8f !important;

    img {
      filter: brightness(0) invert(0.6);
    }
  }
`

/* The antd dropdown is portalled to <body>, so the option styling cannot live
   inside CancelForm. Reason labels run up to 24 characters and would otherwise
   be truncated to a single ellipsised line on a 375px screen. */
const CancelReasonDropdownStyle = createGlobalStyle`
  .cancel-reason-dropdown {
    .ant-select-item-option-content {
      white-space: normal;
      word-break: break-word;
      line-height: 1.5;
    }

    .ant-select-item {
      padding: 11px 12px;
    }
  }
`

export default CancelTreatmentSection
